/**
 * ServicesNav — shared nav for the commercial Photobooth Hire
 * surfaces. Mirrors the editorial homepage nav but adds a
 * "Services ▾" dropdown (per Craig's iter217 nav decision) so the
 * desktop nav stays uncluttered as future categories (dance floors,
 * illuminated numbers, prom letters) land.
 *
 * Order: For Schools → Services ▾ (Photobooth Hire · Venues · For
 * Schools mirror) → Sign In → Begin Your Evening. Mobile collapses
 * Services into a single tap-to-expand row.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  SERIF, IVORY, IVORY_DIM, GOLD, GOLD_FAINT, WALNUT_CARD,
} from './_tokens';

const NAV_LINKS_UNDER_SERVICES = [
  { to: '/photobooth-hire', label: 'Photobooth Hire' },
  { to: '/reviews',         label: 'Reviews' },
  { to: '/events',          label: 'Recent Events' },
  { to: '/venues',          label: 'Venues We Attend' },
  { to: '/schools',         label: 'For Schools' },
];

const ServicesNav = ({ active }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Close the dropdown whenever the route changes.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const quietLink = {
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 14, color: IVORY_DIM,
    textDecoration: 'none',
    letterSpacing: '0.01em',
    transition: 'color 220ms ease',
  };
  const pill = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    height: 40, padding: '0 18px', borderRadius: 999,
    background: 'rgba(18,12,6,0.55)',
    border: `1px solid ${GOLD_FAINT}`,
    backdropFilter: 'blur(10px)',
    color: '#FFE7A8',
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 14, letterSpacing: '0.01em',
    textDecoration: 'none',
    transition: 'border-color 320ms ease, color 320ms ease',
  };

  return (
    <nav
      data-testid="services-nav"
      style={{
        position: 'sticky', top: 0, zIndex: 60,
        background: scrolled ? 'rgba(16,10,6,0.74)' : 'rgba(16,10,6,0.35)',
        backdropFilter: 'blur(14px)',
        borderBottom: `1px solid ${scrolled ? GOLD_FAINT : 'transparent'}`,
        padding: '14px clamp(14px, 4vw, 24px)',
        transition: 'background 480ms ease, border-color 480ms ease',
      }}
    >
      <style>{`
        @media (max-width: 520px) {
          [data-testid="services-nav"] .sn-signin { display: none !important; }
          [data-testid="services-nav"] .sn-cta-full { display: none !important; }
          [data-testid="services-nav"] .sn-cta-short { display: inline !important; }
          [data-testid="services-nav"] [data-testid="services-nav-cta"] {
            padding: 0 14px !important; height: 36px !important; font-size: 13px !important;
          }
        }
      `}</style>
      <div style={{
        maxWidth: 1180, margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 22,
      }}>
        <Link
          to="/"
          data-testid="services-nav-logo"
          style={{
            fontFamily: SERIF,
            fontSize: 'clamp(20px, 5vw, 24px)', fontWeight: 500,
            color: IVORY, textDecoration: 'none',
            letterSpacing: '0.01em', flexShrink: 0,
          }}
        >
          EventDrop <span style={{ color: GOLD, fontSize: 14, marginLeft: 2 }}>✦</span>
        </Link>

        <div ref={wrapRef} style={{
          display: 'flex', alignItems: 'center',
          gap: 'clamp(10px, 2vw, 22px)', flexShrink: 1, minWidth: 0,
        }}>
          {/* Services ▾ dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setOpen(v => !v)}
              data-testid="services-nav-services-toggle"
              aria-expanded={open}
              aria-haspopup="true"
              style={{
                ...quietLink,
                background: 'transparent', border: 'none',
                cursor: 'pointer', padding: 0,
                display: 'inline-flex', alignItems: 'center', gap: 4,
                color: active === 'services' ? IVORY : IVORY_DIM,
              }}
              onMouseEnter={e => { e.currentTarget.style.color = IVORY; }}
              onMouseLeave={e => { e.currentTarget.style.color = active === 'services' ? IVORY : IVORY_DIM; }}
            >
              Services <span style={{ color: GOLD, marginLeft: 4, fontStyle: 'normal' }}>✦</span>
              <span style={{
                fontSize: 10, transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 220ms ease', color: GOLD,
              }}>▾</span>
            </button>
            {open && (
              <div
                data-testid="services-nav-dropdown"
                style={{
                  position: 'absolute', top: 'calc(100% + 14px)', left: '50%',
                  transform: 'translateX(-50%)',
                  minWidth: 220,
                  background: WALNUT_CARD,
                  border: `1px solid ${GOLD_FAINT}`,
                  borderRadius: 10,
                  padding: '10px 6px',
                  boxShadow: '0 22px 60px rgba(0,0,0,0.55)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                {NAV_LINKS_UNDER_SERVICES.map(l => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`services-nav-link-${l.to.replace('/', '')}`}
                    style={{
                      display: 'block',
                      padding: '10px 16px',
                      fontFamily: SERIF, fontStyle: 'italic',
                      fontSize: 14, color: IVORY,
                      textDecoration: 'none',
                      borderRadius: 6,
                      transition: 'background 180ms ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,170,82,0.10)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/login"
            className="sn-signin"
            style={quietLink}
            data-testid="services-nav-signin"
            onMouseEnter={e => { e.currentTarget.style.color = IVORY; }}
            onMouseLeave={e => { e.currentTarget.style.color = IVORY_DIM; }}
          >Sign in</Link>

          <Link
            to="/create"
            data-testid="services-nav-cta"
            style={{ ...pill, whiteSpace: 'nowrap' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = GOLD_FAINT; }}
          >
            <span className="sn-cta-full">Begin your evening</span>
            <span className="sn-cta-short" style={{ display: 'none' }}>Begin</span>
            <span style={{ color: GOLD, marginLeft: 4 }}>✦</span>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default ServicesNav;
